import React from "react";
import { Link } from "react-router-dom";
import { useI18n } from "../../lib/i18n";
import { pickLocale } from "../../lib/format";
import { mediaUrl, sizedMediaUrl } from "../../lib/api";
import AdfImage from "./AdfImage";

/**
 * Shop by category, in the reference design.
 *
 * One tall tile on the left and a 2x2 of smaller ones on the right, each
 * caption burned over the photograph on a bottom-up gradient. Only the first
 * tile marked `large` takes the left column — the reference composition has
 * exactly one, and a second would drop onto its own row and leave the block
 * sprawling, which is what it did on the live site.
 *
 * Every tile comes from admin, already resolved against the catalogue, so a
 * renamed category renames its tile and a deleted one has already been dropped
 * server-side. The section renders nothing at all when there are no tiles.
 */
export default function CollectionGrid({ block }) {
  const { locale } = useI18n();
  if (!block?.tiles?.length) return null;

  const heading = pickLocale(block.title, locale);
  const subtitle = pickLocale(block.subtitle, locale);

  const tiles = block.tiles;
  const leadIndex = Math.max(0, tiles.findIndex((tile) => tile.size === "large"));
  const lead = tiles[leadIndex];
  // Four fill the 2x2; anything beyond it would break the composition, so the
  // grid stops rather than growing a ragged fifth row.
  const rest = tiles.filter((_, index) => index !== leadIndex).slice(0, 4);

  return (
    <section className="py-10 md:py-14 bg-white" data-testid="home-collections">
      <div className="container mx-auto px-6 md:px-12">
        {(heading || subtitle) && (
          <div className="flex justify-between items-end mb-10 md:mb-12">
            <div>
              {heading && (
                <h2 className="text-2xl md:text-3xl font-serif text-charcoal tracking-tight">{heading}</h2>
              )}
              {subtitle && <p className="text-charcoal/60 text-sm mt-2 font-medium">{subtitle}</p>}
            </div>
          </div>
        )}

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          {lead && <Tile tile={lead} locale={locale} variant="lead" priority />}
          <div className="grid grid-cols-2 gap-4">
            {rest.map((tile, index) => (
              <Tile
                key={`${tile.link}-${index}`}
                tile={tile}
                locale={locale}
                variant="small"
                priority={index < 1}
              />
            ))}
          </div>
        </div>
      </div>
    </section>
  );
}

function Tile({ tile, locale, variant, priority }) {
  const title = pickLocale(tile.title, locale);
  const cta = pickLocale(tile.cta_label, locale);
  const lead = variant === "lead";

  return (
    <Link
      to={tile.link}
      className={`relative bg-charcoal/5 group overflow-hidden block ${
        lead ? "h-[60vh] lg:h-auto w-full" : "aspect-[3/4] sm:aspect-square md:aspect-[3/4]"
      }`}
      data-testid="collection-tile"
    >
      {tile.video_url ? (
        /* Plitə klipi: avtomatik, səssiz, loop; şəkil posterdir — video
           yüklənənə qədər o görünür. */
        <video
          src={mediaUrl(tile.video_url)}
          poster={tile.image_url ? sizedMediaUrl(tile.image_url, 720) : undefined}
          autoPlay
          muted
          loop
          playsInline
          preload="metadata"
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
        />
      ) : (
        <AdfImage
          source={tile.image_url}
          mobileSource={tile.mobile_image_url}
          alt=""
          sizes={lead ? "(min-width: 1024px) 50vw, 100vw" : "(min-width: 1024px) 25vw, 50vw"}
          className="absolute inset-0 w-full h-full object-cover transition-transform duration-1000 group-hover:scale-105"
          priority={priority}
        />
      )}
      <div className="absolute inset-0 bg-gradient-to-t from-black/70 via-black/20 to-transparent opacity-90" />
      <div className={`absolute ${lead ? "bottom-8 left-8" : "bottom-6 left-6"}`}>
        <h3
          className={`text-white font-serif tracking-tight ${
            lead ? "text-3xl md:text-4xl mb-4" : "text-xl md:text-2xl mb-3"
          }`}
        >
          {title}
        </h3>
        <span
          className={`text-white uppercase tracking-[0.2em] font-semibold flex items-center ${
            lead ? "text-[10px] gap-3" : "text-[9px] gap-2"
          }`}
        >
          {cta || (locale === "az" ? "KƏŞF ET" : "DISCOVER")}
          <span className={lead ? "text-base leading-none" : "text-sm leading-none"}>→</span>
        </span>
      </div>
    </Link>
  );
}
